import {
  buildCutSequence,
  getSheetSize,
  getStripDeliveryRows,
  shouldShowCutSequence,
  summarizeRepeatedSizes,
} from "@plaatplan/core";
import {
  resolveCoreMessage,
  resolveCutStrategyLabel,
} from "./coreExport.js";

function isStripSelfCutApplicable(selfCut) {
  if (typeof selfCut === "string") {
    return selfCut !== "n.v.t.";
  }
  return selfCut?.key !== "core.stripDelivery.notApplicable";
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 */
export function resolveOffcutLabel(display, strategy, stripsNoCrosscut) {
  return resolveCoreMessage(display, {
    key: strategy === "strips" && stripsNoCrosscut
      ? "core.boardLabel.offcutSelfTrim"
      : "core.boardLabel.offcutDefault",
  });
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 */
export function resolveStripModeBanner(display, strategy, stripsNoCrosscut) {
  if (strategy !== "strips") {
    return null;
  }

  return resolveCoreMessage(display, {
    key: stripsNoCrosscut
      ? "core.boardLabel.stripBannerNoCrosscut"
      : "core.boardLabel.stripBannerCrosscut",
  });
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 * @param {string} placementName
 */
export function resolvePlacementCaption(display, strategy, stripsNoCrosscut, placementName) {
  if (stripsNoCrosscut && strategy === "strips") {
    return display.t("core.boardLabel.placementStripSelfTrim");
  }

  return placementName;
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {object[]|null|undefined} cuts
 * @param {string} strategy
 */
export function resolveCutSequenceSummary(display, cuts, strategy) {
  const count = cuts?.length ?? 0;
  if (count === 0) {
    return "";
  }

  const modeKey = strategy === "strips"
    ? "strips"
    : strategy === "preserve_offcuts"
      ? "preserve"
      : "efficient";

  return resolveCoreMessage(display, {
    key: count === 1 ? "core.cutSummary.singular" : "core.cutSummary.plural",
    params: { count, modeKey },
  });
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {object} sheet
 * @param {object} sourcePanel
 * @param {object} exportDraft
 * @param {string} strategy
 */
export function resolveSheetPrintHeading(display, sheet, sourcePanel, exportDraft, strategy) {
  const { length, width } = getSheetSize(sheet, sourcePanel);
  const parts = [
    display.formatThickness(sheet.thickness),
    display.formatSheet(length, width),
  ];

  if (shouldShowCutSequence(strategy, exportDraft.showCutSequence)) {
    const cuts = buildCutSequence(sheet, sourcePanel, {
      strategy,
      stripsNoCrosscut: exportDraft.stripsNoCrosscut,
      minOffcutLength: Number(exportDraft.minOffcutLength || 500),
      minOffcutWidth: Number(exportDraft.minOffcutWidth || 300),
    });
    const cutSummary = resolveCutSequenceSummary(display, cuts, strategy);
    if (cutSummary) {
      parts.push(cutSummary.replace(/ · /g, ", "));
    }
  }

  return parts.join(", ");
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {object} sheet
 * @param {object} sourcePanel
 * @param {object} exportDraft
 * @param {string} strategy
 * @param {object[]} displayOffcuts
 */
export function resolveSheetPrintSummary(
  display,
  sheet,
  sourcePanel,
  exportDraft,
  strategy,
  displayOffcuts,
) {
  if (strategy === "strips") {
    const stripRows = getStripDeliveryRows(
      { sheets: [sheet] },
      sourcePanel,
      { ...exportDraft, sheetStrategies: { [sheet.index]: "strips" } },
    );

    const stripSummary = summarizeRepeatedSizes(stripRows, "stripSize");
    const finalSummary = summarizeRepeatedSizes(stripRows, "finalSize");
    const selfCutSummary = summarizeRepeatedSizes(
      stripRows.filter((row) => isStripSelfCutApplicable(row.selfCut)),
      "selfCut",
    );

    return [
      stripSummary
        ? display.t("core.printSummary.stripsDeliver", { sizes: stripSummary })
        : display.t("core.printSummary.stripsDefault"),
      finalSummary
        ? display.t("core.printSummary.stripsTrimTo", { sizes: finalSummary })
        : "",
      selfCutSummary
        ? display.t("core.printSummary.stripsSelfCut", { sizes: selfCutSummary })
        : "",
    ].filter(Boolean).join(" ");
  }

  if (strategy === "preserve_offcuts") {
    const primaryOffcut = displayOffcuts?.[0];

    return primaryOffcut
      ? display.t("core.printSummary.preservePrimary", {
        size: display.formatPanelSize(primaryOffcut.length, primaryOffcut.width),
      })
      : display.t("core.printSummary.preserveGeneric");
  }

  return display.t("core.printSummary.efficient");
}

export { resolveCutStrategyLabel };
